import { useEffect, useState } from 'react'
import {
  MANUAL_ASSET_CATEGORIES,
  MANUAL_DEBT_CATEGORIES,
  PROFILE_FIELDS,
  badDates,
  isBlankItem,
  isInferredRow,
  isListedField,
  itemProblems,
  loadDetails,
  saveManual,
  saveProfile,
  toItems,
  userValues,
  type Field,
  type ManualItem,
} from './lib/details'
import { formatDate } from './lib/dates'
import type { Database, Row } from './lib/google/sheets'
import type { Book } from './lib/book'
import { TillerLink } from './TillerLink'

interface Props {
  db: Database
  /** Called after a save, so the dashboard can re-read the Sheet. */
  onSaved: () => void | Promise<unknown>
  onClose: () => void
  /** What the linked Tiller sheet added on the last read. */
  tillerStatus?: Book['tiller']
}

type Save = { state: 'idle' } | { state: 'saving' } | { state: 'saved' } | { state: 'failed'; error: string }

const label = (s: string) => s.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())
const blankItem = (): ManualItem => ({ name: '', type: 'asset', category: '', value: '', as_of: formatDate(new Date()), rate: '', notes: '' })

export function ProfilePage({ db, onSaved, onClose, tillerStatus }: Props) {
  const [loaded, setLoaded] = useState(false)
  const [loadError, setLoadError] = useState<string>()
  const [values, setValues] = useState<Record<string, string>>({})
  const [facts, setFacts] = useState<{ key: string; value: string }[]>([])
  const [originalUserKeys, setOriginalUserKeys] = useState<string[]>([])
  const [inferred, setInferred] = useState<Row[]>([])
  const [items, setItems] = useState<ManualItem[]>([])
  const [profileSave, setProfileSave] = useState<Save>({ state: 'idle' })
  const [manualSave, setManualSave] = useState<Save>({ state: 'idle' })

  const absorb = (profile: Row[]) => {
    const mine = userValues(profile)
    setValues(Object.fromEntries(Object.entries(mine).filter(([k]) => isListedField(k))))
    setFacts(Object.entries(mine).filter(([k]) => !isListedField(k)).map(([key, value]) => ({ key, value })))
    setOriginalUserKeys(Object.keys(mine))
    setInferred(profile.filter(isInferredRow))
  }

  useEffect(() => {
    loadDetails(db)
      .then(({ profile, manual }) => {
        absorb(profile)
        setItems(toItems(manual))
        setLoaded(true)
      })
      .catch((e) => setLoadError((e as Error).message))
  }, [db])

  const submitProfile = async () => {
    const bad = badDates(values)
    if (bad.length) {
      setProfileSave({ state: 'failed', error: `Write ${bad.join(' and ')} as YYYY-MM-DD.` })
      return
    }
    setProfileSave({ state: 'saving' })
    const all: Record<string, string> = {}
    for (const g of PROFILE_FIELDS) for (const f of g.fields) all[f.key] = values[f.key] ?? ''
    for (const k of originalUserKeys) if (!(k in all)) all[k] = '' // removed facts
    for (const f of facts) if (f.key.trim()) all[f.key.trim().toLowerCase().replace(/\s+/g, '_')] = f.value
    try {
      absorb(await saveProfile(db, all))
      setProfileSave({ state: 'saved' })
      onSaved()
    } catch (e) {
      setProfileSave({ state: 'failed', error: (e as Error).message })
    }
  }

  const submitManual = async () => {
    if (items.some((i) => !isBlankItem(i) && itemProblems(i).length)) {
      setManualSave({ state: 'failed', error: 'Fix the rows marked below first.' })
      return
    }
    setManualSave({ state: 'saving' })
    try {
      await saveManual(db, items)
      setManualSave({ state: 'saved' })
      onSaved()
    } catch (e) {
      setManualSave({ state: 'failed', error: (e as Error).message })
    }
  }

  const correct = (r: Row) => {
    const key = String(r.key)
    if (isListedField(key)) setValues((v) => ({ ...v, [key]: String(r.value ?? '') }))
    else setFacts((f) => [...f.filter((x) => x.key !== key), { key, value: String(r.value ?? '') }])
    setProfileSave({ state: 'idle' })
  }

  if (loadError) return <p className="section error">✕ {loadError}</p>
  if (!loaded) return <p className="section muted">Loading your profile…</p>

  const setItem = (i: number, patch: Partial<ManualItem>) => {
    setItems((list) => list.map((it, j) => (j === i ? { ...it, ...patch } : it)))
    setManualSave({ state: 'idle' })
  }

  return (
    <>
      <section className="section">
        <div className="row">
          <h2>Your profile</h2>
          <button className="quiet" onClick={onClose}>← Back to dashboard</button>
        </div>
        <p className="muted">
          Everything here is saved to your spreadsheet and used by the review and the question box. Leave anything blank you’d rather not
          share. After saving, press <b>Re-analyse</b> on the dashboard to get a review that uses it.
        </p>
      </section>

      <section className="section">
        <h2>Facts about you</h2>
        <div className="profile-groups">
          {PROFILE_FIELDS.map((g) => (
            <fieldset key={g.title} className="card">
              <legend className="label">{g.title}</legend>
              {g.fields.map((f) => (
                <FieldInput
                  key={f.key}
                  field={f}
                  value={values[f.key] ?? ''}
                  onChange={(v) => {
                    setValues((all) => ({ ...all, [f.key]: v }))
                    setProfileSave({ state: 'idle' })
                  }}
                />
              ))}
            </fieldset>
          ))}
        </div>

        <h3 className="subhead">Other facts</h3>
        <p className="muted small">Anything else Claude should know, as a short name and a value.</p>
        <ul className="facts">
          {facts.map((f, i) => (
            <li key={i}>
              <input aria-label="Fact name" placeholder="e.g. pension" value={f.key} onChange={(e) => setFacts((all) => all.map((x, j) => (j === i ? { ...x, key: e.target.value } : x)))} />
              <input aria-label="Value" placeholder="e.g. 3400 a month from age 65" value={f.value} onChange={(e) => setFacts((all) => all.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))} />
              <button className="quiet" aria-label={`Remove ${f.key || 'fact'}`} onClick={() => setFacts((all) => all.filter((_, j) => j !== i))}>✕</button>
            </li>
          ))}
        </ul>
        <button className="quiet" onClick={() => setFacts((all) => [...all, { key: '', value: '' }])}>+ Add a fact</button>

        <div className="save-row">
          <button onClick={submitProfile} disabled={profileSave.state === 'saving'}>{profileSave.state === 'saving' ? 'Saving…' : 'Save profile'}</button>
          <SaveStatus save={profileSave} />
        </div>
      </section>

      {inferred.length > 0 && (
        <section className="section">
          <h2>Claude’s assumptions</h2>
          <p className="muted small">What the last review inferred from your statements. Correct one and it becomes your fact; Claude never overwrites those.</p>
          <table className="table">
            <thead><tr><th>Assumption</th><th>Value</th><th>Based on</th><th /></tr></thead>
            <tbody>
              {inferred.map((r) => (
                <tr key={String(r.key)}>
                  <td>{label(String(r.key))}</td>
                  <td>{String(r.value ?? '')}</td>
                  <td className="muted">{String(r.basis ?? '')}</td>
                  <td className="r"><button className="quiet" onClick={() => correct(r)}>Correct</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <TillerLink status={tillerStatus} onChanged={async () => onSaved()} />

      <section className="section">
        <h2>Things without statements</h2>
        <p className="muted small">
          Your home, a car loan, a 401(k) you don’t upload: each counts in net worth, the balance sheet and the review. Enter a debt as the amount owed.
        </p>
        <div className="scroll">
          <table className="table manual-table">
            <thead>
              <tr><th>Name</th><th>Type</th><th>Category</th><th className="r">Value ($)</th><th>As of</th><th className="r">Rate (%)</th><th>Notes</th><th /></tr>
            </thead>
            <tbody>
              {items.map((it, i) => {
                const problems = itemProblems(it)
                const touched = it.name || it.value
                return (
                  <tr key={i} className={touched && problems.length ? 'invalid' : ''}>
                    <td>
                      <input aria-label="Name" value={it.name} placeholder="e.g. House" onChange={(e) => setItem(i, { name: e.target.value })} />
                      {touched && problems.length > 0 && <div className="problem">! {problems.join(', ')}</div>}
                    </td>
                    <td>
                      <select aria-label="Type" value={it.type} onChange={(e) => setItem(i, { type: e.target.value as ManualItem['type'], category: '' })}>
                        <option value="asset">asset</option>
                        <option value="debt">debt</option>
                      </select>
                    </td>
                    <td>
                      <select aria-label="Category" value={it.category} onChange={(e) => setItem(i, { category: e.target.value })}>
                        <option value="">—</option>
                        {(it.type === 'debt' ? MANUAL_DEBT_CATEGORIES : MANUAL_ASSET_CATEGORIES).map((c) => <option key={c} value={c}>{c}</option>)}
                        {it.category && ![...MANUAL_ASSET_CATEGORIES, ...MANUAL_DEBT_CATEGORIES].includes(it.category) && <option value={it.category}>{it.category}</option>}
                      </select>
                    </td>
                    <td><input aria-label="Value" className="num r" inputMode="decimal" value={it.value} placeholder="e.g. 1150000" onChange={(e) => setItem(i, { value: e.target.value.replace(/[$,\s]/g, '') })} /></td>
                    <td><input aria-label="As of" className="num" value={it.as_of} placeholder="YYYY-MM-DD" onChange={(e) => setItem(i, { as_of: e.target.value.trim() })} /></td>
                    <td><input aria-label="Rate" className="num r" inputMode="decimal" value={it.rate} disabled={it.type !== 'debt'} placeholder={it.type === 'debt' ? 'e.g. 5.9' : ''} onChange={(e) => setItem(i, { rate: e.target.value.replace(/[%\s]/g, '') })} /></td>
                    <td><input aria-label="Notes" value={it.notes} onChange={(e) => setItem(i, { notes: e.target.value })} /></td>
                    <td className="r"><button className="quiet" aria-label={`Remove ${it.name || 'row'}`} onClick={() => { setItems((list) => list.filter((_, j) => j !== i)); setManualSave({ state: 'idle' }) }}>✕</button></td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <div className="save-row">
          <button className="quiet" onClick={() => setItems((list) => [...list, blankItem()])}>+ Add an asset or debt</button>
          <button onClick={submitManual} disabled={manualSave.state === 'saving'}>{manualSave.state === 'saving' ? 'Saving…' : 'Save list'}</button>
          <SaveStatus save={manualSave} />
        </div>
      </section>
    </>
  )
}

function FieldInput({ field, value, onChange }: { field: Field; value: string; onChange: (v: string) => void }) {
  const id = `f-${field.key}`
  return (
    <div className="field">
      <label htmlFor={id}>{field.label}</label>
      {field.input === 'select' ? (
        <select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">—</option>
          {field.options!.map((o) => <option key={o} value={o}>{o}</option>)}
          {value && !field.options!.includes(value) && <option value={value}>{value}</option>}
        </select>
      ) : field.input === 'textarea' ? (
        <textarea id={id} rows={2} value={value} placeholder={field.placeholder} onChange={(e) => onChange(e.target.value)} />
      ) : (
        // Plain text, not type="date": the browser's date picker shows dates in the
        // computer's locale (mm/dd/yyyy), and every date here is YYYY-MM-DD.
        <input
          id={id}
          type="text"
          className={field.input === 'date' ? 'num' : undefined}
          inputMode={field.input === 'number' ? 'numeric' : undefined}
          value={value}
          placeholder={field.input === 'date' ? 'YYYY-MM-DD' : field.placeholder}
          onChange={(e) => onChange(field.input === 'number' ? e.target.value.replace(/[$,\s]/g, '') : e.target.value)}
        />
      )}
      {field.help && <span className="help">{field.help}</span>}
    </div>
  )
}

function SaveStatus({ save }: { save: Save }) {
  if (save.state === 'saved') return <span className="saved">✓ Saved to your spreadsheet</span>
  if (save.state === 'failed') return <span className="error">✕ {save.error}</span>
  return null
}
